import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Plus, Minus } from "lucide-react";
import Reveal from "@/components/site/Reveal";

const FAQS = [
  {
    q: "Quanto costa lavorare con te?",
    a: "I servizi singoli hanno il prezzo scritto qui sul sito: 100€ o 350€. I percorsi continuativi si valutano sulla tua struttura: prima la guardo, poi ti dico il prezzo. Niente sorprese dopo.",
  },
  {
    q: "Devo cambiare annunci o siti di prenotazione?",
    a: "No. Lavoro su quello che hai già: Booking, Airbnb o entrambi. Se serve sistemare qualcosa, lo facciamo insieme.",
  },
  {
    q: "E se poi non mi trovo bene?",
    a: "Ne parliamo prima di iniziare, con chiarezza: niente clausole nascoste, niente vincoli che scopri dopo. Il mio lavoro deve convincerti, non trattenerti.",
  },
  {
    q: "Cosa succede dopo la chiamata gratuita?",
    a: "Ti dico cosa ho visto guardando la tua struttura e se posso aiutarti. Se ha senso, partiamo. Se no, ci stringiamo la mano e resta tutto gratis.",
  },
  {
    q: "Gli account e le recensioni restano miei?",
    a: "Sì, tutto. Gli account Booking e Airbnb sono intestati a te, le recensioni restano sul tuo profilo per sempre, e con l'app inclusa che tiene allineati i calendari controlli tutto da un solo posto. Io lavoro dentro il tuo sistema: tu vedi ogni mossa, ogni giorno.",
  },
  {
    q: "Ti occupi anche di pulizie e check-in?",
    a: "No: io mi occupo di prezzi, calendario e strategia — la parte che decide quanto guadagni. Pulizie e accoglienza restano a te, o a chi scegli tu.",
  },
];

export default function Faq() {
  const [openIndex, setOpenIndex] = useState(null);

  return (
    <section id="domande-frequenti" data-testid="faq-section" className="border-b border-[#2A241F] py-12 sm:py-28 lg:py-36">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <Reveal>
          <p className="section-kicker">Domande frequenti</p>
          <h2 className="mt-4 font-heading text-3xl font-black uppercase leading-[1.05] tracking-tight text-[#FAF7F2] sm:text-4xl lg:text-5xl">
            Le domande che mi fanno sempre
          </h2>
        </Reveal>

        <div className="mt-14 max-w-4xl">
          {FAQS.map((faq, i) => {
            const open = openIndex === i;
            return (
              <Reveal key={faq.q} delay={i * 0.05}>
                <div data-testid={`faq-item-${i + 1}`} className="border-t border-[#2A241F] last:border-b">
                  <button
                    data-testid={`faq-toggle-${i + 1}`}
                    onClick={() => setOpenIndex(open ? null : i)}
                    aria-expanded={open}
                    className="group flex w-full items-center justify-between gap-6 py-6 text-left"
                  >
                    <span className="text-base font-medium text-[#FAF7F2] transition-colors duration-300 group-hover:text-[#E05A2C] sm:text-lg">
                      {faq.q}
                    </span>
                    {open ? <Minus className="h-5 w-5 shrink-0 text-[#E05A2C]" /> : <Plus className="h-5 w-5 shrink-0 text-[#A89F91] transition-colors duration-300 group-hover:text-[#E05A2C]" />}
                  </button>
                  <AnimatePresence initial={false}>
                    {open && (
                      <motion.div
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: "auto", opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.45, ease: [0.16, 1, 0.3, 1] }}
                        className="overflow-hidden"
                      >
                        <p data-testid={`faq-answer-${i + 1}`} className="max-w-2xl pb-7 text-sm leading-relaxed text-[#C4BBB0] sm:text-base">
                          {faq.a}
                        </p>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              </Reveal>
            );
          })}
        </div>
      </div>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "FAQPage",
            mainEntity: FAQS.map((f) => ({
              "@type": "Question",
              name: f.q,
              acceptedAnswer: { "@type": "Answer", text: f.a },
            })),
          }),
        }}
      />
    </section>
  );
}
